import { clsx } from 'clsx';
import { FC, HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import GanttBarTooltip from './GanttBarTooltip';
import { GanttTaskCapabilityTooltip } from './GanttTimePicker';
import { Handle } from './Handle';
import { IconType } from 'react-icons';
import { useDebouncedCallback } from 'use-debounce';

interface BarProps extends HTMLAttributes<HTMLDivElement> {
  color: string;
  slotCount: number;
  startSlot: number;
  endSlot: number;
  onStartHandleDrag?: (event: MouseEvent) => void;
  onEndHandleDrag?: (event: MouseEvent) => void;
  onDragBar?: (startEvent: MouseEvent) => (event: MouseEvent) => void;
  readOnly?: boolean;
  onDelete?: () => void;
  sub?: boolean;
  Icon?: IconType;
  iconColor?: string;
  tooltip?: string;
  resizeable?: boolean;
  ghost?: boolean;
  capabilityTooltip?: GanttTaskCapabilityTooltip;
  maxSlotLength?: boolean;
  minSlotLength?: boolean;
}

const BAR_HEIGHT_PERCENT = 64;

export const Bar: FC<BarProps> = ({
  color,
  className,
  slotCount,
  startSlot,
  endSlot,
  onStartHandleDrag,
  onEndHandleDrag,
  onDragBar,
  onDelete,
  sub,
  readOnly,
  Icon,
  iconColor = 'black',
  tooltip,
  capabilityTooltip,
  resizeable = true,
  ghost = false,
  maxSlotLength = false,
  minSlotLength = false
}) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const splashZoneRef = useRef<HTMLDivElement>(null);

  // How far along the container the bar should start.
  let startPercent = (startSlot / slotCount) * 100;

  // How much of the container the bar should take up.
  // NOTE: We need to add a 1 here, because both the start and end slot are included in the width.
  // eg. If the bar starts in slot 0, and ends in slot 2, it spans slot 0, 1 and 2, which is three slots.
  const lengthInSlots = endSlot - startSlot + 1;
  let widthPercent = (lengthInSlots / slotCount) * 100;
  const zeroLengthSlot = lengthInSlots === 0;

  // Another weird bit.
  // If the bars run off the end of either side of the screen, we need to fudge the numbers so that
  // they are only just too big.

  // This means the borders don't get displayed (as intended), but icons and tooltips get put in
  // sensible places.
  if (endSlot > slotCount || startSlot < 0) {
    widthPercent = ((lengthInSlots - 1) / slotCount) * 100 + 0.1;
  }

  if (startSlot < 0) {
    startPercent = -0.1;
  }

  const heightPercent = sub ? BAR_HEIGHT_PERCENT * 0.6 : BAR_HEIGHT_PERCENT;
  const style = {
    left: `calc(${startPercent}% - ${zeroLengthSlot ? 4 : 0}px)`,
    width: !zeroLengthSlot ? `${widthPercent}%` : '12px',
    height: `${heightPercent}%`,
    top: `${(100 - heightPercent) / 2}%`,
    zIndex: zeroLengthSlot ? '50' : undefined,
    borderColor: color,
    cursor: 'pointer'
  };

  const onMouseUp = useCallback(
    (func: (event: MouseEvent) => void) => () => {
      window.removeEventListener('mousemove', func);
    },
    []
  );

  const onMouseDown = useCallback(
    (event: MouseEvent) => {
      if (!onDragBar) {
        return;
      }

      if (window.getSelection) {
        window.getSelection()?.removeAllRanges();
      }

      const newDragEvent = onDragBar(event);

      window.addEventListener('mousemove', newDragEvent);
      window.addEventListener('mouseup', onMouseUp(newDragEvent), { once: true });
    },
    [onDragBar, onMouseUp]
  );

  useEffect(() => {
    if (!onDragBar) {
      return;
    }

    const element = innerRef.current;

    element?.addEventListener('mousedown', onMouseDown);

    return () => {
      element?.removeEventListener('mousedown', onMouseDown);
    };
  }, [onMouseDown, onMouseUp, onDragBar, innerRef]);

  const leftAlignTooltips = startPercent < 7;

  const [hovering, setHovering] = useState<boolean>(false);

  const debouncedSetHover = useDebouncedCallback(setHovering, 150);

  useEffect(() => {
    const barElement = innerRef.current;
    const fullElement = outerRef.current;
    const splashZone = splashZoneRef.current;

    const mouseIn = () => debouncedSetHover(true);
    const mouseOut = () => {
      debouncedSetHover(false);
    };

    barElement?.addEventListener('mouseover', mouseIn);
    fullElement?.addEventListener('mouseover', mouseIn);
    splashZone?.addEventListener('mouseover', mouseIn);
    fullElement?.addEventListener('mouseout', mouseOut);
    splashZone?.addEventListener('mouseout', mouseOut);

    return () => {
      barElement?.removeEventListener('mouseover', mouseIn);
      fullElement?.removeEventListener('mouseout', mouseOut);
      fullElement?.removeEventListener('mousein', mouseIn);
      splashZone?.removeEventListener('mouseout', mouseOut);
      splashZone?.removeEventListener('mousein', mouseIn);
    };
  }, [debouncedSetHover]);

  return (
    <div
      ref={outerRef}
      style={style}
      className={clsx(
        className,
        'absolute flex group z-20',
        ghost ? 'border-dashed' : '',
        ghost ? (sub ? 'border-2' : 'border-4') : undefined,
        ghost && startSlot < 0 ? 'border-l-0' : '',
        ghost && endSlot > slotCount ? 'border-r-0' : ''
      )}
    >
      {!readOnly && resizeable && onStartHandleDrag && (
        <div className={clsx('w-fit', sub ? 'z-30' : 'rounded-sm h-full', hovering ? 'block' : 'hidden')}>
          <Handle
            internal={startSlot === 0}
            start
            onDragHandle={onStartHandleDrag}
            sub={sub}
            cursor={
              maxSlotLength
                ? 'cursor-e-resize'
                : minSlotLength
                  ? 'cursor-w-resize'
                  : resizeable
                    ? 'cursor-ew-resize'
                    : 'cursor-none'
            }
          />
        </div>
      )}
      <div
        ref={innerRef}
        style={{ background: color, borderColor: color, opacity: ghost ? 0.3 : undefined }}
        className={clsx(
          'flex z-20',
          sub ? 'border-l border-r' : 'rounded-sm h-full',
          zeroLengthSlot ? 'w-[4px] mx-auto' : 'w-full'
        )}
      >
        {Icon &&
          (zeroLengthSlot ? (
            <div style={{ color: 'black' }} className="absolute bottom-[-10px] left-[-7px] z-[1000]">
              <Icon />
            </div>
          ) : (
            <div style={{ color: iconColor }} className="flex items-center justify-center w-full">
              <Icon className="h-6 w-6" />
            </div>
          ))}

        {capabilityTooltip && tooltip && (
          <GanttBarTooltip
            className={lengthInSlots <= 1 ? 'absolute top-[-15px]' : 'flex items-start'}
            capabilityTooltip={capabilityTooltip}
            taskTooltip={{
              text: tooltip,
              color,
              bgColor: iconColor
            }}
          />
        )}
      </div>
      {!readOnly && resizeable && onEndHandleDrag && (
        <div className={clsx('w-fit', sub ? 'z-30' : 'rounded-sm h-full', hovering ? 'block' : 'hidden')}>
          <Handle
            onDragHandle={onEndHandleDrag}
            sub={sub}
            cursor={
              maxSlotLength
                ? 'cursor-w-resize'
                : minSlotLength
                  ? 'cursor-e-resize'
                  : resizeable
                    ? 'cursor-ew-resize'
                    : 'cursor-none'
            }
          />
        </div>
      )}
      {onDelete && !readOnly && sub && (
        <div
          className={clsx(`absolute rounded-sm items-center z-50`, hovering ? 'flex' : 'hidden')}
          style={{
            whiteSpace: 'nowrap',
            right: leftAlignTooltips ? undefined : '3px',
            left: leftAlignTooltips ? '3px' : undefined,
            top: '0.1rem'
          }}
        >
          {onDelete && (
            <button
              onMouseDown={event => {
                event.preventDefault();
                onDelete();
              }}
            >
              <FiX className="h-[1.3rem] w-[1.3rem] text-white"></FiX>
            </button>
          )}
        </div>
      )}
      {tooltip && !capabilityTooltip && (
        <span
          className={clsx(
            'select-none text-xs rounded-md font-semibold px-1 absolute z-50 top-[-10px]',
            hovering ? 'block' : 'hidden'
          )}
          style={{
            background: color,
            color: iconColor,
            whiteSpace: 'nowrap',
            right: leftAlignTooltips ? undefined : '0.44rem',
            left: leftAlignTooltips ? '0.44rem' : undefined
          }}
        >
          {tooltip}
        </span>
      )}
      {!sub && (
        <div
          ref={splashZoneRef}
          className="absolute h-full"
          style={{ width: 'calc(100% + 30px)', left: -15 }}
        ></div>
      )}
    </div>
  );
};
